<template>
  <div class="order-type-dropdown-wrapper" v-if="orderTypes?.length > 0">
    <!-- Custom dropdown trigger -->
    <div
      :class="[isOnCartPage ? 'order-type-header-cart' : 'order-type-header']"
      @click="toggleDropdown"
    >
      <span class="fs-12 fw-700 order-type-title-text">{{ titleText }}</span>
      <span class="fs-12 fw-400 ml4" v-if="!isHeaderCompressed || isOnCartPage">
        {{ titleAdditionalText }}
      </span>
      <i class="hkfi-arrow-line fs-14 ml4 mt4 fw-700 rotate90"></i>
    </div>

    <!-- Custom dropdown menu (floating just below the button) -->
    <div v-if="isDropdownVisible" class="order-type-dropdown">
      <div
        v-for="(item, i) in orderTypes"
        :key="i"
        class="order-type-dropdown-option"
        :class="[
          selectedOrderType == item.orderType
            ? 'color-slaty'
            : 'color-slaty-dark',
        ]"
        @click="handleOrderTypeClick(item, i)"
      >
        <span class="fs-12 fw-700">{{ getOrderTypeDisplay(item) }}</span>
        <span class="fs-11 fw-400 ml4">
          {{ getOrderTypeAdditionalDisplay(item) }}
        </span>
      </div>

      <!-- Show "Now / Later" if selected type is Delivery -->
      <div
        v-if="
          selectedOrderType === 'D' && isOnCartPage && isAdvanceOrderAvailable
        "
        class="order-type-timing-wrapper"
      >
        <div class="fs-12 fw-700 order-type-dropdown-option dashed-border-top">
          Delivery Date & Time
        </div>
        <div
          class="mt8 inline-flex align-items-center order-type-dropdown-option w100"
        >
          <div
            class="timing-option"
            :class="[!advanceDate && !advanceTime ? 'selected' : '']"
            @click="enableScheduleDelivery()"
          >
            <i
              :class="
                !advanceDate && !advanceTime
                  ? 'hkfi-tick-icon color-white'
                  : 'hkfi-circle-outline'
              "
            ></i>
          </div>

          <span class="ml8 fs-12 fw-700">Now</span>
        </div>
        <div
          class="mt8 inline-flex align-items-center order-type-dropdown-option w100"
        >
          <div
            class="timing-option"
            :class="[advanceDate && advanceTime ? 'selected' : '']"
            @click="enableScheduleDelivery()"
          >
            <i
              :class="
                advanceDate && advanceTime
                  ? 'hkfi-tick-icon color-white'
                  : 'hkfi-circle-outline'
              "
            ></i>
          </div>
          <span v-if="advanceDate && advanceTime" class="ml8 fs-12 fw-700"
            >{{ advanceDate }} {{ advanceTime }}</span
          >
          <span v-else class="ml8 fs-12 fw-700">Later</span>
        </div>
      </div>
    </div>

    <!-- Click outside area -->
    <div
      v-if="isDropdownVisible"
      class="menu-drop-down-outerDiv"
      @click.self="checkClickedOutside($event)"
      @click.stop
    ></div>
  </div>
</template>

<script>
import { keyMapsMutation } from "../helpers/storeKeys";
import LocalStorageFunc from "../helpers/localStorageFunc";
import Utility from "../helpers/utility";
import {
  updateAmplitudeUserProperties,
  EVENT_CLICK_ORDERTYPE,
} from "../helpers/analyticsEvents";
import { isAdvanceOrderAvailable } from "../config/apiEndpoints";
import {
  EVENT_ADVANCE_ORDER_CLICK,
  EVENT_ORDER_MODE_CART,
  EVENT_ORDER_MODE_SELECTION,
} from "../helpers/analyticsEvents";
import { OrderTypesCode } from "../helpers/orderTypesConstants";

export default {
  name: "OrderTypeDropdown",
  props: ["dataResults"],
  data() {
    return {
      orderTypes: [],
      isDropdownVisible: false, // control dropdown visibility
      isHeaderCompressed: false,
    };
  },
  mounted() {
    this.$store.commit(keyMapsMutation.LOAD_CURRENT_ORDER_TYPE);
    if (!this.isOnLocationPage) {
      this.$store.commit(
        keyMapsMutation.UPDATE_SELECTED_ORDER_TYPE,
        this.currentOrderType,
      );
    }
    this.orderTypes = Utility.tryJsonParse(this.dataResults, []);
    if (!this.isOnCartPage) {
      window.addEventListener("scroll", this.handleStyleChangesOnScroll);
    }
  },
  methods: {
    toggleDropdown() {
      this.isDropdownVisible = !this.isDropdownVisible;
    },
    handleOrderTypeClick(item, index) {
      if (this.isOnCartPage) {
        EVENT_ORDER_MODE_CART({
          userAction: "click",
          currentMode: this.selectedOrderType,
          updatedMode: item.orderType,
          distanceDiff:
            item.orderType === "D"
              ? "NA"
              : this.displayDistanceOrTimeActive + " Km away",
          deliveryTime:
            item.orderType === "D"
              ? this.displayDistanceOrTimeActive + " mins"
              : "NA",
        });
      } else {
        EVENT_ORDER_MODE_SELECTION({
          userAction: "click",
          currentMode: this.selectedOrderType,
          updatedMode: item.orderType,
          distanceDiff:
            item.orderType === "D"
              ? "NA"
              : this.displayDistanceOrTimeActive + " Km away",
          deliveryTime:
            item.orderType === "D"
              ? this.displayDistanceOrTimeActive + " mins"
              : "NA",
        });
      }
      this.selectedOrderType = item.orderType;
      this.$store.commit(
        keyMapsMutation.UPDATE_SELECTED_ORDER_TYPE,
        item.orderType,
      );
      this.$store.commit(
        keyMapsMutation.UPDATE_SHOW_MANUAL_LOCATION_SCREEN,
        false,
      );

      EVENT_CLICK_ORDERTYPE({
        positionWithinSection: index + 1,
        category: item.title,
        orderType: item.orderType,
      });
      updateAmplitudeUserProperties({ orderType: item.orderType });

      Utility.sendToLocationScreen({ changeTriggerFromDineinTwy: false });
      this.isDropdownVisible = false; // Hide dropdown after selection
    },
    getOrderTypeDisplay(item) {
      if (!item) return "Select Order Type";
      return item.title;
    },
    getOrderTypeAdditionalDisplay(item) {
      if (!item) return "";
      let displayText = "";

      if (
        item.orderType !== "D" &&
        this.displayDistanceOrTimeActive &&
        this.displayDistanceOrTimeActive > 0
      ) {
        displayText += `${this.displayDistanceOrTimeActive} Km away`;
      } else if (
        item.orderType !== "D" &&
        this.currentLocation?.lat &&
        this.storeLocation?.lat
      ) {
        displayText += `${this.getDistanceDiffText} away`;
      } else if (
        item.orderType === "D" &&
        this.displayETA &&
        this.displayETA !== "NA"
      ) {
        let etaText = this.displayETA.toString();
        let isImmediate = etaText.toLowerCase() === "now" || etaText === "0";
        displayText += isImmediate ? "in --" : `in ${this.displayETA}`;
      }
      return displayText;
    },
    checkClickedOutside(e) {
      if (this.isDropdownVisible) {
        this.isDropdownVisible = false;
      }
    },
    handleStyleChangesOnScroll() {
      let header = window.document.getElementById("home-header");
      let themeDefault = window.document.getElementById("themeDefault");
      if (window.pageYOffset > 1 && !this.isHeaderCompressed && header) {
        header.classList.remove("header-expanded");
        header.classList.add("compressed-sticky");
        themeDefault.classList.add("mt62");
        this.isHeaderCompressed = true;
      } else if (window.pageYOffset < 1 && this.isHeaderCompressed && header) {
        header.classList.add("header-expanded");
        header.classList.remove("compressed-sticky");
        themeDefault.classList.remove("mt62");
        this.isHeaderCompressed = false;
      }
    },
    enableScheduleDelivery() {
      EVENT_ADVANCE_ORDER_CLICK({
        eventAction: "Open",
        eventCategory:
          !this.advanceDate && !this.advanceTime
            ? "Deliver Now"
            : "Advance Order",
        eventLabel:
          this.deliveryText +
          " " +
          (this.advanceDate ? this.advanceDate : "") +
          (this.advanceTime ? this.advanceTime : "") +
          (!this.advanceDate && !this.advanceTime
            ? this.orderFulfilmentTime
            : ""),
        sectionName: "Deliver Now or Later Address Strip",
        orderType: this.$store.state.User.currentOrderType,
        subSection:
          !this.advanceDate && !this.advanceTime
            ? "Deliver Now"
            : "Advance Order",
      });
      this.$store.commit(keyMapsMutation.UPDATE_SHOW_SCHEDULE_DELIVERY, true);
    },
  },
  computed: {
    selectedOrderType() {
      return this.isOnLocationPage
        ? LocalStorageFunc.getSelectedDeliveryType() ||
            this.$store.state.User.currentOrderType
        : this.$store.state.User.selectedOrderType ||
            this.$store.state.User.currentOrderType;
    },
    currentOrderType() {
      return this.$store.state.User.currentOrderType;
    },
    closeTakeAwayListingModal() {
      this.$store.commit(keyMapsMutation.UPDATE_IS_TAKEWAY_SELECTED, false);
    },
    deliveryTypeStationData() {
      return LocalStorageFunc.getDeliveryTypeStationData();
    },
    displayDistanceOrTimeActive() {
      return LocalStorageFunc.getDeliveryTypeStationData()?.driveDistanceInKm;
    },
    currentLocation() {
      if (
        this.selectedOrderType === "D" &&
        this.isRecentAvailableForTakeawayAndDinein
      ) {
        return {
          lat: this.lastLocationNewRecent?.addressComponent?.latitude,
          lng: this.lastLocationNewRecent?.addressComponent?.longitude,
        };
      }
      return this.$store.state.Location.currentGPSLocationData?.geometry
        ?.location;
    },
    lastLocationNewRecent() {
      return LocalStorageFunc.getLastLocationNew()?.[0]?.recent;
    },
    isRecentAvailableForTakeawayAndDinein() {
      let codes = this.lastLocationNewRecent?.allowedDeliveryTypeCodes || [];
      return (
        codes.includes(OrderTypesCode.TAKEAWAY_CODE) &&
        codes.includes(OrderTypesCode.DINE_IN_CODE)
      );
    },
    storeLocation() {
      if (
        this.selectedOrderType === "D" &&
        this.isRecentAvailableForTakeawayAndDinein
      ) {
        return {
          lat: this.lastLocationNewRecent?.latitude,
          lng: this.lastLocationNewRecent?.longitude,
        };
      }
      let storeLoc = {};
      if (this.deliveryTypeStationData?.latitude) {
        storeLoc.lat = this.deliveryTypeStationData?.latitude;
        storeLoc.lng = this.deliveryTypeStationData?.longitude;
      }
      return storeLoc;
    },
    getDistanceDiff() {
      return this.currentLocation?.lat && this.storeLocation?.lat
        ? Utility.distanceBetweenTwoCoordinates(
            this.currentLocation,
            this.storeLocation,
          )
        : 0;
    },
    getDistanceDiffText() {
      let diff = this.getDistanceDiff;
      let dist =
        Math.trunc(diff) > 1000
          ? `${(Math.trunc(diff) / 1000).toFixed(1)}Km`
          : `${Math.trunc(diff)}m`;
      return dist;
    },
    sgInfo() {
      return Object.keys(this.$store.state.Cart.sgInfo).length > 0
        ? this.$store.state.Cart.sgInfo
        : LocalStorageFunc.getSgInfoDeliveryData();
    },
    displayETA() {
      return this.sgInfo?.displayETA;
    },
    isOnLocationPage() {
      return Utility.isCurrentlyAtUrl("/location");
    },
    titleText() {
      let selectedType = this.orderTypes.find(
        (item, i) => item.orderType == this.selectedOrderType,
      );
      return this.getOrderTypeDisplay(selectedType);
    },
    titleAdditionalText() {
      let selectedType = this.orderTypes.find(
        (item, i) => item.orderType == this.selectedOrderType,
      );
      return this.getOrderTypeAdditionalDisplay(selectedType);
    },
    advanceDate() {
      return this.$store.state.Cart.advanceOrderTimeSlot?.date;
    },
    advanceTime() {
      return this.$store.state.Cart.advanceOrderTimeSlot?.time;
    },
    orderFulfilmentTime() {
      return this.$store.state.Cart.sgInfo?.orderFulfilmentTime;
    },
    isOnCartPage() {
      return Utility.isCurrentlyAtUrl("/cart");
    },
    isAdvanceOrderAvailable() {
      return isAdvanceOrderAvailable;
    },
  },
};
</script>
